import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, ShoppingCartIcon, FileTextIcon, AlertTriangleIcon, CheckCircleIcon, TrendingUpIcon, ActivityIcon } from 'lucide-react';
import { mockSuppliers, mockPRs, mockPOs, mockProducts } from '../data/mockData';
const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendLabel,
  colorClass
}: any) => <motion.div initial={{
  opacity: 0,
  y: 20
}} animate={{
  opacity: 1,
  y: 0
}} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center" data-id="element-621">
    <div className={`p-4 rounded-lg ${colorClass} mr-4`} data-id="element-622">
      <Icon className="h-6 w-6 text-white" data-id="element-623" />
    </div>
    <div data-id="element-624">
      <p className="text-sm font-medium text-gray-500" data-id="element-625">{title}</p>
      <div className="flex items-baseline" data-id="element-626">
        <p className="text-2xl font-semibold text-gray-900" data-id="element-627">{value}</p>
        {trend && <p className={`ml-2 text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-628">
            {trend > 0 ? '+' : ''}
            {trend}%
          </p>}
      </div>
      {trendLabel && <p className="text-xs text-gray-400 mt-1" data-id="element-629">{trendLabel}</p>}
    </div>
  </motion.div>;
export function Dashboard() {
  return <div className="space-y-6" data-id="element-630">
      <div className="flex items-center justify-between" data-id="element-631">
        <div data-id="element-632">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-633">
            Dashboard Overview
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-634">
            Welcome back, here's what's happening today.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-635">
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-636">
            + New Requisition
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-637">
        <StatCard title="Total Suppliers" value={mockSuppliers.length} icon={UsersIcon} trend={12} trendLabel="vs last month" colorClass="bg-blue-500" data-id="element-638" />
        <StatCard title="Active PRs" value={mockPRs.filter(pr => pr.status !== 'Approved' && pr.status !== 'Rejected').length} icon={ShoppingCartIcon} trend={-5} trendLabel="vs last month" colorClass="bg-indigo-500" data-id="element-639" />
        <StatCard title="Pending Approvals" value={5} icon={CheckCircleIcon} colorClass="bg-amber-500" data-id="element-640" />
        <StatCard title="Open POs" value={mockPOs.length} icon={FileTextIcon} trend={8} trendLabel="vs last month" colorClass="bg-emerald-500" data-id="element-641" />
        <StatCard title="Low Stock Alerts" value={mockProducts.filter(p => p.status === 'Active').length} icon={AlertTriangleIcon} colorClass="bg-red-500" data-id="element-642" />
        <StatCard title="Procurement Spend" value="$1.2M" icon={TrendingUpIcon} trend={15} trendLabel="vs last year" colorClass="bg-purple-500" data-id="element-643" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-644">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-645">
          <div className="flex items-center justify-between mb-4" data-id="element-646">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-647">
              Recent Activity
            </h2>
            <ActivityIcon className="h-5 w-5 text-gray-400" data-id="element-648" />
          </div>
          <div className="flow-root" data-id="element-649">
            <ul className="-mb-8" data-id="element-650">
              {[{
              content: 'Purchase Order PO-2023-001 approved by',
              target: 'Finance Head',
              date: '2 hours ago',
              icon: CheckCircleIcon,
              iconBg: 'bg-green-500'
            }, {
              content: 'New Supplier registration submitted by',
              target: 'TechCorp Solutions',
              date: '4 hours ago',
              icon: UsersIcon,
              iconBg: 'bg-blue-500'
            }, {
              content: 'Purchase Requisition PR-2023-002 created by',
              target: 'Mike Ross',
              date: '1 day ago',
              icon: ShoppingCartIcon,
              iconBg: 'bg-indigo-500'
            }, {
              content: 'Low stock alert triggered for',
              target: 'Laptop Pro X',
              date: '1 day ago',
              icon: AlertTriangleIcon,
              iconBg: 'bg-red-500'
            }].map((event, eventIdx) => <li key={eventIdx} data-id="element-651">
                  <div className="relative pb-8" data-id="element-652">
                    {eventIdx !== 3 ? <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-653" /> : null}
                    <div className="relative flex space-x-3" data-id="element-654">
                      <div data-id="element-655">
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${event.iconBg}`} data-id="element-656">
                          <event.icon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-657" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4" data-id="element-658">
                        <div data-id="element-659">
                          <p className="text-sm text-gray-500" data-id="element-660">
                            {event.content}{' '}
                            <span className="font-medium text-gray-900" data-id="element-661">
                              {event.target}
                            </span>
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500" data-id="element-662">
                          <time dateTime={event.date} data-id="element-663">{event.date}</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-664">
          <div className="flex items-center justify-between mb-4" data-id="element-665">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-666">
              Pending Approvals
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-667">
              5 Requires Action
            </span>
          </div>
          <div className="space-y-4" data-id="element-668">
            {[{
            id: 'PR-2023-002',
            type: 'Purchase Requisition',
            amount: '$15,000',
            dept: 'HR-02'
          }, {
            id: 'SUP-003',
            type: 'Supplier Registration',
            amount: '-',
            dept: 'BuildIt Construction'
          }, {
            id: 'RFP-2023-001',
            type: 'RFP Approval',
            amount: '-',
            dept: 'IT Infrastructure'
          }].map(item => <div key={item.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-669">
                <div className="flex items-center" data-id="element-670">
                  <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-671">
                    <FileTextIcon className="h-5 w-5 text-indigo-600" data-id="element-672" />
                  </div>
                  <div className="ml-4" data-id="element-673">
                    <p className="text-sm font-medium text-gray-900" data-id="element-674">
                      {item.id}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-675">
                      {item.type} • {item.dept}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-676">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-677">
                    {item.amount}
                  </span>
                  <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium" data-id="element-678">
                    Review
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}